import { uploadDsd } from "@/apis/dsd-dsd-compare/route";
import type { UploadDsdResponse } from "@/apis/dsd-dsd-compare/types";
import { DsdFile, DsdFileStatus } from "@/types/dsd";
import { useAcceptedFileSelect } from "@/hooks/use-accepted-file-select";
import { cn } from "@/lib/utils";
import { popModal, pushModal } from "@/stores/modal";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation } from "@tanstack/react-query";
import { FolderSymlink, Upload } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { DsdSelectModal, DsdSelectModalType } from "../dsd-select-modal";

export interface DsdEmptyStateProps {
  /** 모달 타입: 기준(base) 또는 비교 대상(compare) */
  modalType: DsdSelectModalType;
  /** 필터링 기준 파일 (상대편 파일이 선택된 경우) */
  filterByFile?: DsdFile | null;
  /** 파일 선택 완료 핸들러 */
  onFileSelect?: (file: DsdFile) => void;
  className?: string;
}

// 백엔드 period → 분기 코드(Q1~Q4) 매핑
const PERIOD_MAP: Record<string, string> = {
  사업: "Q4",
  "1분기": "Q1",
  반기: "Q2",
  "3분기": "Q3",
};

// 백엔드 reportType → financialStatementType 매핑
const REPORT_TYPE_MAP: Record<string, string> = {
  연결: "CONSOLIDATED",
  별도: "SEPARATE",
};

// UploadDsdResponse → DsdFile 변환
const uploadResponseToDsdFile = (res: UploadDsdResponse): DsdFile => {
  // "2025년도" → 2025
  const yearMatch = res.year.match(/(\d{4})/);
  const reportYear = yearMatch ? parseInt(yearMatch[1], 10) : null;

  return {
    id: res.fileId,
    uploadedFileId: res.fileId,
    name: res.filename,
    displayName: res.filename,
    creator: res.uploaderEmail,
    revision: 0,
    prevWorkbookId: null,
    versionNo: 1,
    reportQuarter: PERIOD_MAP[res.period] ?? null,
    reportYear,
    financialStatementType: REPORT_TYPE_MAP[res.reportType] ?? res.reportType,
    companyCode: null,
    createdAt: res.createdAt,
    updatedAt: res.createdAt,
    label: res.filename,
    status: DsdFileStatus.DSD_ANALYZED,
  };
};

// 허용 확장자 (.dsd 파일만)
const ACCEPTED_FILE_TYPES = ".dsd";

export const DsdEmptyState = ({
  modalType,
  filterByFile,
  onFileSelect,
  className,
}: DsdEmptyStateProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: analyze, isPending } = useMutation({
    mutationKey: ["dsd-dsd-compare", "upload"],
    mutationFn: async (file: File): Promise<DsdFile> => {
      const result = await uploadDsd(file);
      return uploadResponseToDsdFile(result);
    },
    onSuccess: (dsdFile: DsdFile) => {
      toast.success("DSD 보고서가 업로드되었습니다.");
      onFileSelect?.(dsdFile);
    },
    onError: () => {
      toast.error(
        "DSD 파일을 업로드할 수 없습니다. 올바른 DSD 파일인지 확인해주세요.",
      );
    },
  });

  const handleFileSelect = useAcceptedFileSelect({
    accept: ACCEPTED_FILE_TYPES,
    inputRef: fileInputRef,
    onFileSelect: analyze,
  });

  // 파일 업로드 버튼 클릭
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // 파일 선택 시 분석 실행
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files?.[0]);
    // input 초기화 (같은 파일 재선택 가능하도록)
    event.target.value = "";
  };

  // 기존 DSD 선택 버튼 클릭 시 모달 열기
  const handleSelectClick = () => {
    pushModal(DsdSelectModal, {
      type: modalType,
      filterByFile,
      onSelect: onFileSelect,
      onClose: () => popModal(),
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col flex-1 items-center justify-center gap-4 px-[19px] py-[19px]",
        "bg-background-gray-alternative border border-dashed border-line-normal rounded-md",
        className,
      )}
    >
      {/* 숨겨진 파일 input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileChange}
        className="hidden"
      />

      {/* 새 파일 업로드 섹션 */}
      <div className="flex flex-col items-center gap-3">
        <p className="label-2-medium text-label-alternative">
          새로운 보고서를 업로드 하세요.
        </p>
        <Button
          variant="solid"
          color="secondary"
          size={32}
          leftIcon={<Upload size={18} />}
          onClick={handleUploadClick}
          disabled={isPending}
        >
          {isPending ? "업로드 중..." : "새 파일 업로드"}
        </Button>
      </div>

      {/* 또는 구분선 */}
      <div className="relative w-full h-[19px]">
        <div className="absolute inset-x-0 top-1/2 border-t border-line-normal" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center bg-background-gray-normal px-1">
          <span className="label-2-medium text-label-alternative">또는</span>
        </div>
      </div>

      {/* 기존 파일 불러오기 섹션 */}
      <div className="flex flex-col items-center gap-3">
        <p className="label-2-medium text-label-alternative">
          기존에 업로드한 보고서를 불러오세요.
        </p>
        <Button
          variant="outlined"
          size={32}
          leftIcon={<FolderSymlink size={14} />}
          onClick={handleSelectClick}
          disabled={isPending}
        >
          이전 파일 불러오기
        </Button>
      </div>
    </div>
  );
};
